import { memo, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  accountActions,
  getAccountState,
  LoginState,
} from "../../redux/slices/accountSlice";
import { isDesktop } from "react-device-detect";
import {
  hasSubscription,
  userPhoneVerificationStatus,
} from "@markit/common.utils";
import { useNavigate } from "react-router-dom";
import PhoneOnboardingSelectType from "../../components/Subscription/PhoneOnboarding/PhoneOnboardingSelectType";
import PhoneOnboardingEnterInfo from "../../components/Subscription/PhoneOnboarding/PhoneOnboardingEnterInfo";
import { firebaseMapsApiKey } from "../../firebase";
import { loadGoogleMapsScript } from "../../components/CreateEvent/CreateModals/CreateLocationModal";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { Colors } from "../../utils/colors";
import MarkitLogoBlue from "../../assets/MarkitLogoBlue.png";
import { useTheme } from "../../hooks/useTheme";
import { Icon } from "@iconify/react";
import { useOnMount } from "../../hooks/useOnMount";
import LoadingScreen from "../LoadingScreen";
import {
  TwilioBusinessType,
  TwilioPhoneVerificationStatus,
  VerificationInfo,
} from "@markit/common.types";
import { NavigationId } from "../../navigation/AppParamList";

export enum PhoneOnboardingStage {
  SELECT_TYPE = "Select Type",
  ENTER_INFO = "Enter Info",
}

export enum VerificationAccountType {
  BUSINESS = "Business",
  INDIVIDUAL = "Individual",
}

const SubscriptionPhoneOnboarding = memo(
  function SubscriptionPhoneOnboardingFn() {
    const { accountData, appInitialized, loggedIn } =
      useSelector(getAccountState).account;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [phoneOnboardingStage, setPhoneOnboardingStage] =
      useState<PhoneOnboardingStage>(PhoneOnboardingStage.SELECT_TYPE);
    const [selectedAccountType, setSelectedAccountType] =
      useState<VerificationAccountType>(VerificationAccountType.BUSINESS);
    const [onboardingInfo, setOnboardingInfo] = useState<VerificationInfo>({
      businessRegistrationNumber: "",
      businessType: TwilioBusinessType.LLC,
      businessCity: "",
      businessContactEmail: accountData.email,
      businessContactFirstName: "",
      businessContactLastName: "",
      businessContactPhone: accountData.phoneNumber,
      businessName: "",
      businessPostalCode: "",
      businessStateProvinceRegion: "",
      businessStreetAddress: "",
      businessWebsite: "",
      messageVolume: "10",
    });
    const [phoneNumber, setPhoneNumber] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingMaps, setLoadingMaps] = useState(true);

    const styles = {
      backButton: {
        backgroundColor: Colors.WHITE1,
        borderRadius: 100,
        height: 35,
        width: 35,
        border: `1px solid ${theme.DividerColor.borderColor}`,
        cursor: "pointer",
      },
    };

    useOnMount(() => {
      loadGoogleMapsScript(firebaseMapsApiKey, () => {
        setLoadingMaps(false);
      });
    });

    const isBusinessAccount = useMemo(
      () => selectedAccountType === VerificationAccountType.BUSINESS,
      [selectedAccountType]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useAsyncEffect(async () => {
      if (loggedIn !== LoginState.LOGGED_IN) {
        dispatch(accountActions.setRedirectPath(location.pathname));
        navigate(NavigationId.SIGN_IN);
        return;
      }
      if (appInitialized && loading) {
        if (!hasSubscription(accountData)) {
          alert("You do not have a subscription!");
          navigate("/home");
        }
        if (
          userPhoneVerificationStatus(accountData) !==
          TwilioPhoneVerificationStatus.UNVERIFIED
        ) {
          alert("You have already submitted info for your account.");
          navigate("/home");
        }
        setLoading(false);
      }
    }, [accountData, appInitialized, dispatch, loading, loggedIn, navigate]);

    const selectAccountTypeOnPress = useCallback(() => {
      if (selectedAccountType === VerificationAccountType.INDIVIDUAL) {
        // Initialize some fields for if individual is selected
        setOnboardingInfo({
          ...onboardingInfo,
          businessRegistrationNumber: "",
          businessType: TwilioBusinessType.SOLE_PROPRIETOR,
          businessName: accountData.fullName,
        });
      }
      setPhoneOnboardingStage(PhoneOnboardingStage.ENTER_INFO);
    }, [accountData.fullName, onboardingInfo, selectedAccountType]);

    const switchAccountTypeOnPress = useCallback(
      (newType: VerificationAccountType) => {
        setSelectedAccountType(newType);
        if (newType === VerificationAccountType.INDIVIDUAL) {
          setOnboardingInfo({
            ...onboardingInfo,
            businessRegistrationNumber: "",
            businessType: TwilioBusinessType.SOLE_PROPRIETOR,
            businessName: accountData.fullName,
          });
        } else {
          setOnboardingInfo({
            ...onboardingInfo,
            businessType: TwilioBusinessType.LLC,
            businessName: "",
          });
        }
      },
      [accountData.fullName, onboardingInfo]
    );

    const internalBackButton = useMemo(
      () => (
        <div style={isDesktop ? { position: "absolute", left: 20 } : {}}>
          <div
            className="Centering"
            style={styles.backButton}
            onClick={() => {
              if (phoneOnboardingStage === PhoneOnboardingStage.SELECT_TYPE) {
                navigate("/home");
              } else {
                setPhoneOnboardingStage(
                  phoneOnboardingStage === PhoneOnboardingStage.ENTER_INFO
                    ? PhoneOnboardingStage.SELECT_TYPE
                    : PhoneOnboardingStage.ENTER_INFO
                );
              }
            }}
          >
            <Icon
              icon="ion:arrow-back"
              height={24}
              style={{ color: Colors.BLACK }}
            />
          </div>
        </div>
      ),
      [navigate, phoneOnboardingStage, styles.backButton]
    );

    const renderContent = useMemo(
      () =>
        !loadingMaps ? (
          <div
            className={isDesktop ? "AlignedColumn" : "ColumnNormal"}
            style={{
              paddingInline: isDesktop ? 0 : 14,
              paddingTop: 60,
              paddingBottom: 100,
              gap: 30,
              position: "relative",
            }}
          >
            {internalBackButton}
            <div
              className={isDesktop ? "AlignedColumn" : "ColumnNormal"}
              style={{ gap: 20 }}
            >
              <div>
                <img
                  style={{ height: 20, width: "auto", objectFit: "cover" }}
                  src={MarkitLogoBlue}
                  alt={"markitLogoBlue"}
                />
              </div>
              <div
                className={isDesktop ? "AlignedColumn" : "ColumnNormal"}
                style={{ gap: 12 }}
              >
                <span style={{ fontSize: 20, fontWeight: 500 }}>
                  {phoneOnboardingStage === PhoneOnboardingStage.SELECT_TYPE
                    ? "Continue as a business or individual?"
                    : `${
                        isBusinessAccount ? "Business" : "Individual"
                      } Account Details`}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: Colors.GRAY2,
                    textAlign: isDesktop ? "center" : "left",
                    width: isDesktop ? 540 : undefined,
                  }}
                >
                  {phoneOnboardingStage === PhoneOnboardingStage.SELECT_TYPE ? (
                    <span>
                      We require additional information to submit to phone
                      carriers to verify your identity and activate your Markit
                      phone number. Accurate information is required for
                      approval.
                      <br />
                      <br />
                      This information is shared strictly with our carrier
                      partner, Twilio, and is never exposed publicly.
                    </span>
                  ) : (
                    <span>
                      {isBusinessAccount ? "Don't have" : "Have"} an EIN number?
                      Continue with{" "}
                      <span
                        style={{
                          fontWeight: 500,
                          color: Colors.BLACK,
                          textDecorationLine: "underline",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          switchAccountTypeOnPress(
                            isBusinessAccount
                              ? VerificationAccountType.INDIVIDUAL
                              : VerificationAccountType.BUSINESS
                          )
                        }
                      >
                        {isBusinessAccount ? "Individual" : "Business"}
                      </span>{" "}
                      instead.
                    </span>
                  )}
                </span>
              </div>
            </div>
            {phoneOnboardingStage === PhoneOnboardingStage.SELECT_TYPE ? (
              <PhoneOnboardingSelectType
                selectedAccountType={selectedAccountType}
                setSelectedAccountType={setSelectedAccountType}
                selectAccountTypeOnPress={selectAccountTypeOnPress}
              />
            ) : phoneOnboardingStage === PhoneOnboardingStage.ENTER_INFO ? (
              <PhoneOnboardingEnterInfo
                selectedAccountType={selectedAccountType}
                onboardingInfo={onboardingInfo}
                setOnboardingInfo={setOnboardingInfo}
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
              />
            ) : null}
          </div>
        ) : null,
      [
        loadingMaps,
        internalBackButton,
        phoneOnboardingStage,
        switchAccountTypeOnPress,
        isBusinessAccount,
        selectedAccountType,
        selectAccountTypeOnPress,
        onboardingInfo,
        phoneNumber,
      ]
    );

    if (loading) {
      return <LoadingScreen isLoadingPage={loading} />;
    }

    return isDesktop ? (
      <div className="ColumnNormal" style={{ height: "100vh" }}>
        {renderContent}
      </div>
    ) : (
      renderContent
    );
  }
);

export default SubscriptionPhoneOnboarding;
